import createClient from 'openapi-fetch';
import type { Shema } from '../api/odjemalec';
import type { paths } from '../api/shema';
import { napakaPovezave } from '../api/napaka';

/**
 * Odjemalec za javno ponudbo vozil (/api/javno/**): brez seje in žetona, ker strani gledajo obiskovalci (tudi
 * vgrajene na tujih straneh), in brez zaklepa naročnine, ki velja samo za prijavljene.
 */
export const javniApi = createClient<paths>({
  baseUrl: typeof window === 'undefined' ? '' : window.location.origin,
  fetch: async (zahteva: Request) => {
    try {
      return await fetch(zahteva);
    } catch {
      throw napakaPovezave();
    }
  },
});

/** Naslov slike javnega vozila ({@code mala} za seznam). */
export function slikaUrl(oznaka: string, id: number, mala = false): string {
  return `/api/javno/ponudba/${encodeURIComponent(oznaka)}/slike/${id}${mala ? '?mala=true' : ''}`;
}

export function logoUrl(oznaka: string): string {
  return `/api/javno/ponudba/${encodeURIComponent(oznaka)}/logo`;
}

/** Telefonska številka za povezavo tel: (samo števke in +). */
export function telefonUrl(telefon: string): string {
  return `tel:${telefon.replace(/[^0-9+]/g, '')}`;
}

/** Stran je vgrajena na spletni strani prodajalca (?vgrajeno=1): povezave ohranijo parameter. */
export function vgrajeno(iskanje: string): boolean {
  return new URLSearchParams(iskanje).get('vgrajeno') === '1';
}

/**
 * Iskalnikom prepove indeksiranje strani (vozilo ni več na voljo), da vseh enakih strani »Vozilo ni več na voljo« ne
 * označijo kot dvojnike; vrne funkcijo, ki prepoved odstrani. Strežnik hera-web (docker/streznik.mjs) tako stran že
 * vrne s statusom 404 in noindex; to velja, kadar glave strani ni (razvoj, hera-server ne odgovori).
 */
export function prepreciIndeksiranje(): () => void {
  let m = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (!m) {
    m = document.createElement('meta');
    m.name = 'robots';
    document.head.appendChild(m);
  }
  m.content = 'noindex';
  return () => m.remove();
}

/** Oblike za {@code koliko} (»1 vozilo«, »2 vozili«, »3 vozila«, »5 vozil«). */
export const VOZILA: [string, string, string, string] = ['vozilo', 'vozili', 'vozila', 'vozil'];

/** Vrstni red na seznamu vozil (zaloga prodajalca ga uredi na strani, ponudba vseh na strežniku). */
export type Vrstni = 'novo' | 'cena-gor' | 'cena-dol';

type Vozilo = Shema['JavnoVozilo'];

/**
 * Vozila, ki ustrezajo iskanju (naziv, povzetek in {@code dodatno}, npr. prodajalec), v izbranem vrstnem redu; brez
 * cene so pri razvrščanju po ceni na koncu.
 */
export function prikazanaVozila(
  vozila: Vozilo[],
  iskanje: string,
  vrstni: Vrstni,
  dodatno: (v: Vozilo) => string = () => '',
): Vozilo[] {
  const iskano = iskanje.trim().toLowerCase();
  return vozila
    .filter((v) => !iskano || `${v.naziv} ${v.povzetek} ${dodatno(v)}`.toLowerCase().includes(iskano))
    .sort((a, b) => {
      if (vrstni === 'novo' || (a.cena == null && b.cena == null)) {
        return 0;
      }
      // vozila brez javne cene (»Cena po dogovoru«) so vedno na koncu
      if (a.cena == null || b.cena == null) {
        return a.cena == null ? 1 : -1;
      }
      return (a.cena - b.cena) * (vrstni === 'cena-gor' ? 1 : -1);
    });
}
